import { useContext } from 'react'

import ScreenLayout from '@/components/layout/ScreenLayout'
import { useInShell } from '@/components/layout/shell-context'
import { SectionStripContext } from '@/components/ui/section-strip-context'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

/**
 * A TitleRow's box with a bar where the words go: the 36px line of
 * `text-h-2xl`, so a real TitleRow replaces it without moving the body.
 * For a pending page whose title is not known until the data is — most
 * titles are, and a pending page should then render the real TitleRow.
 * Under the section tab strip, from `lg` up, it holds no box: the strip
 * names the page there, and the title it stands for is screen-reader only.
 */
export function TitleRowSkeleton() {
  const underStrip = useContext(SectionStripContext)
  return (
    <div className={cn('flex h-9 items-center', underStrip && 'lg:hidden')}>
      <Skeleton className="h-6 w-44 rounded-sm" />
    </div>
  )
}

/**
 * The router's `defaultPendingComponent`: what any route without its own
 * `pendingComponent` shows while its code or its loader is still out — from
 * a frame after the click, its blocks painting only past 80ms (ScreenLayout's
 * `pending`).
 * A route that has been converted to a loader always brings its own,
 * shaped like its loaded layout (OverviewScreen's `OverviewPending` is the
 * model) — this is the stopgap for the ones that have not, which today means
 * a lazy chunk that is slow to arrive.
 *
 * Inside the shell it keeps the page's frame: ScreenLayout with the shared
 * rail (already cached by the `_app` loader, so it does not flicker), a
 * title bar and two generic blocks. It cannot know the real shape, so it
 * does not pretend to — the blocks are deliberately plain.
 *
 * Outside the shell (the auth and onboarding routes, or the `_app` layout's
 * own chunk on a cold load) it draws nothing but the page background: there
 * is no layout it could promise there, and ScreenLayout's rail would make
 * signed-in requests on a signed-out screen. See shell-context.ts.
 */
export default function PendingPage() {
  const inShell = useInShell()

  if (!inShell) {
    return (
      <div aria-busy="true" className="min-h-dvh bg-background">
        <span role="status" className="sr-only">
          Loading…
        </span>
      </div>
    )
  }

  return (
    <ScreenLayout pending header={<TitleRowSkeleton />}>
      <Skeleton className="h-64 w-full rounded-lg" />
      <Skeleton className="h-96 w-full rounded-lg" />
    </ScreenLayout>
  )
}
